/**
 * Accès à l'API du module Justificatifs. Aucune donnée n'est fabriquée ici :
 * tout vient du serveur, et les erreurs portent son message (`ErreurApi`).
 */

import type { CompteursEtats, EtatJustificatif, PointSuivi, SyntheseJustificatifs } from '@scc/core';
import { ErreurApi } from '../../api.js';

/** Refus de l'API : son texte, son statut et, pour un dépôt refusé, le motif (`depot_inactif`…). */
export class ErreurPiece extends ErreurApi {
  constructor(
    message: string,
    statut: number,
    readonly motif?: string,
  ) {
    super(message, statut);
    this.name = 'ErreurPiece';
  }
}

async function echec(r: Response): Promise<never> {
  const corps = (await r.json().catch(() => ({}))) as { erreur?: string; motif?: string };
  throw new ErreurPiece(corps.erreur ?? `Erreur ${r.status}`, r.status, corps.motif);
}

async function lire<T>(chemin: string): Promise<T> {
  const r = await fetch(chemin, { headers: { accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) await echec(r);
  return (await r.json()) as T;
}

async function envoyer<T>(methode: string, chemin: string, corps: unknown): Promise<T> {
  const r = await fetch(chemin, {
    method: methode,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(corps),
    credentials: 'same-origin',
  });
  if (!r.ok) await echec(r);
  return (await r.json()) as T;
}

export type { CompteursEtats, EtatJustificatif, PointSuivi, SyntheseJustificatifs };
export type ChampRegle = 'tiers' | 'libelle' | 'compte' | 'compte_libelle' | 'type_transaction';
export type OperateurRegle = 'egal' | 'contient' | 'commence_par';
export type SensRegle = 'recette' | 'depense' | 'tous';
export type StatutRegle = 'proposee' | 'active' | 'inactive';

/** Comptes de la collecte des factures : des nombres seulement, visibles en lecture. */
export interface ResumeCollecte {
  readonly aValider: number;
  readonly aArbitrer: number;
  readonly aTelecharger: number;
  readonly deposees30j: number;
}

export interface EtatJustificatifs {
  readonly exercice: number;
  readonly collecte: ResumeCollecte;
  readonly synthese: SyntheseJustificatifs;
  readonly suivi: readonly PointSuivi[];
  readonly derniereIngestion: string | null;
}

export interface LigneJustificatif {
  readonly id: string;
  readonly date: string;
  readonly montant: number;
  readonly sens: 'recette' | 'depense';
  readonly libelle: string;
  readonly tiers: string;
  readonly compteLibelle: string;
  readonly typeOperation: string;
  readonly etat: EtatJustificatif;
  readonly regleId: number | null;
  readonly motif: string | null;
  readonly rapproche: boolean;
  readonly lienZoho: string | null;
}

export interface CritereRegle {
  readonly champ: ChampRegle;
  readonly operateur: OperateurRegle;
  readonly valeur: string;
  readonly sens: SensRegle;
  readonly montantMax: number | null;
}

export interface RegleDispense extends CritereRegle {
  readonly id: number;
  readonly motif: string;
  readonly statut: StatutRegle;
  readonly origine: 'proposee' | 'manuelle';
  readonly creePar: string;
  readonly modifiePar: string;
  readonly modifieLe: string;
  /** Lignes sans pièce de l'exercice courant que la règle capte, quel que soit son statut. */
  readonly lignes: number;
}

export interface FiltreLignes {
  readonly exercice: number;
  readonly etat?: EtatJustificatif;
  readonly sens?: 'recette' | 'depense';
  readonly mois?: string;
}

export function etatJustificatifs(exercice?: number): Promise<EtatJustificatifs> {
  return lire(`/api/justificatifs${exercice === undefined ? '' : `?exercice=${String(exercice)}`}`);
}

export function lignesJustificatifs(f: FiltreLignes): Promise<{ lignes: LigneJustificatif[] }> {
  const q = new URLSearchParams({ exercice: String(f.exercice) });
  if (f.etat) q.set('etat', f.etat);
  if (f.sens) q.set('sens', f.sens);
  if (f.mois) q.set('mois', f.mois);
  return lire(`/api/justificatifs/lignes?${q.toString()}`);
}

export function reglesDispense(): Promise<{ regles: RegleDispense[] }> {
  return lire('/api/justificatifs/regles');
}

export function creerRegleDispense(r: CritereRegle & { motif: string }): Promise<{ regle: RegleDispense }> {
  return envoyer('POST', '/api/justificatifs/regles', r);
}

export function modifierRegleDispense(
  id: number,
  m: Partial<CritereRegle> & { motif?: string; statut?: StatutRegle },
): Promise<{ regle: RegleDispense }> {
  return envoyer('PATCH', `/api/justificatifs/regles/${String(id)}`, m);
}

export function testerRegleDispense(
  r: CritereRegle,
): Promise<{ lignes: number; exemples: { id: string; date: string; montant: number; libelle: string; tiers: string }[] }> {
  return envoyer('POST', '/api/justificatifs/regles/tester', r);
}

// ── Pièces reçues (collecte des factures) ───────────────────────────────

export type EtatPiece = 'a_valider' | 'a_arbitrer' | 'a_telecharger' | 'deposee' | 'deja_classee' | 'ecartee' | 'retiree';
export type NaturePiece = 'facture_scc' | 'autre_structure' | 'facture_emise' | 'pas_une_facture' | 'douteuse';
export type NatureEcartee = 'autre_structure' | 'facture_emise' | 'pas_une_facture';
export type ProvenanceChamp = 'regle' | 'texte' | 'courriel' | 'saisie';

export interface ChampPiece<T> {
  readonly valeur: T | null;
  readonly provenance: ProvenanceChamp | null;
  /** Lu avec une confiance faible : à vérifier. */
  readonly douteux: boolean;
}

export interface ChampsPiece {
  readonly fournisseur: ChampPiece<string>;
  /** `AAAA-MM-JJ`. */
  readonly date: ChampPiece<string>;
  readonly montant: ChampPiece<number>;
  readonly devise: ChampPiece<string>;
  readonly numero: ChampPiece<string>;
}

export interface Piece {
  readonly id: number;
  readonly etat: EtatPiece;
  readonly nature: NaturePiece;
  readonly origineTri: string;
  readonly regleId: number | null;
  readonly typeMime: string;
  readonly taille: number;
  readonly nomOrigine: string;
  readonly champs: ChampsPiece | null;
  readonly rubrique: string | null;
  readonly nomPropose: string | null;
  readonly exercice: number | null;
  readonly driveId: string | null;
  readonly driveLien: string | null;
  readonly motif: string | null;
  readonly creeLe: string;
  readonly modifieLe: string;
  readonly traitePar: string | null;
  /** Un fichier est conservé sur le serveur. */
  readonly fichier: boolean;
}

export interface ProvenancePiece {
  readonly id: number;
  readonly sourceId: string;
  readonly boite: string;
  readonly expediteur: string;
  readonly sujet: string;
  readonly date: string;
  readonly lien: string;
}

export interface PieceListee extends Piece {
  /** Le premier message qui a apporté la pièce. */
  readonly provenance: ProvenancePiece | null;
  readonly nbProvenances: number;
}

export interface RubriquePossible {
  readonly numero: string;
  readonly libelle: string;
  readonly driveId: string;
}

export interface DetailPiece {
  readonly piece: Piece;
  readonly provenances: ProvenancePiece[];
  readonly rubriquesPossibles: RubriquePossible[];
}

export interface CorrectionPiece {
  nature?: NaturePiece;
  champs?: {
    fournisseur?: string | null;
    date?: string | null;
    montant?: number | null;
    devise?: string | null;
    numero?: string | null;
  };
  rubrique?: string | null;
  nom?: string;
}

export interface DepotDrive {
  readonly driveId: string;
  readonly lien: string;
  readonly nom: string;
  readonly dejaPresent: boolean;
}

const PIECES = '/api/justificatifs/pieces';

export const urlFichierPiece = (id: number): string => `${PIECES}/${String(id)}/fichier`;

/** `origine` : l'origine du tri (`jev` pour relire ce que Jev a écarté). */
export function piecesParEtat(etat: EtatPiece, origine?: string): Promise<{ pieces: PieceListee[] }> {
  return lire(`${PIECES}?etat=${etat}${origine === undefined ? '' : `&origine=${origine}`}`);
}

export function detailPiece(id: number): Promise<DetailPiece> {
  return lire(`${PIECES}/${String(id)}`);
}

export function corrigerPiece(id: number, c: CorrectionPiece): Promise<{ piece: Piece }> {
  return envoyer('PATCH', `${PIECES}/${String(id)}`, c);
}

export function deposerPiece(id: number): Promise<DepotDrive> {
  return envoyer('POST', `${PIECES}/${String(id)}/deposer`, {});
}

export function ecarterPiece(id: number, motif: string, nature?: NatureEcartee): Promise<{ piece: Piece }> {
  return envoyer('POST', `${PIECES}/${String(id)}/ecarter`, nature === undefined ? { motif } : { motif, nature });
}

export function reprendrePiece(id: number): Promise<{ piece: Piece }> {
  return envoyer('POST', `${PIECES}/${String(id)}/reprendre`, {});
}

export function retirerPiece(id: number, motif?: string): Promise<{ piece: Piece }> {
  return envoyer('POST', `${PIECES}/${String(id)}/retiree`, motif === undefined ? {} : { motif });
}

/** Taille maximale d'un fichier déposé (la même que le serveur). */
export const TAILLE_DEPOT_MAX = 20 * 1024 * 1024;

/**
 * Dépose un fichier : les octets tels quels, le nom dans l'en-tête `X-Nom-Fichier`.
 * `aTelecharger` : la demande de téléchargement à laquelle le fichier répond.
 */
export async function deposerFichier(fichier: File, aTelecharger?: number): Promise<{ piece: Piece; doublon: boolean }> {
  const r = await fetch(`${PIECES}/depot`, {
    method: 'POST',
    headers: {
      'content-type': 'application/octet-stream',
      'x-nom-fichier': encodeURIComponent(fichier.name),
      ...(aTelecharger === undefined ? {} : { 'x-piece-a-telecharger': String(aTelecharger) }),
    },
    body: fichier,
    credentials: 'same-origin',
  });
  if (!r.ok) await echec(r);
  return (await r.json()) as { piece: Piece; doublon: boolean };
}

export function lancerCampagne(): Promise<{ lancee: true }> {
  return envoyer('POST', `${PIECES}/campagne`, {});
}

export type StatutRegleCollecte = 'proposee' | 'active' | 'inactive';

export interface SaisieRegleCollecte {
  expediteur: string | null;
  boite: string | null;
  sujetContient: string | null;
  fichierContient: string | null;
  nature: NaturePiece;
  fournisseur: string | null;
  rubrique: string | null;
  depotAuto: boolean;
  jamaisJev: boolean;
  statut: StatutRegleCollecte;
}

export interface RegleCollecte extends SaisieRegleCollecte {
  readonly id: number;
  readonly creeLe: string;
  readonly creePar: string;
  /** Pièces que la règle a triées. */
  readonly captees: number;
}

export type CriteresRegleCollecte = Pick<SaisieRegleCollecte, 'expediteur' | 'boite' | 'sujetContient' | 'fichierContient'>;

export interface ApercuRegleCollecte {
  readonly pieces: number;
  readonly exemples: readonly {
    id: number;
    etat: EtatPiece;
    nature: NaturePiece;
    nomOrigine: string;
    expediteur: string;
    sujet: string;
    date: string;
  }[];
}

export function reglesCollecte(): Promise<{ regles: RegleCollecte[] }> {
  return lire(`${PIECES}/regles`);
}

export function creerRegleCollecte(r: Partial<SaisieRegleCollecte> & { nature: NaturePiece }): Promise<{ regle: RegleCollecte }> {
  return envoyer('POST', `${PIECES}/regles`, r);
}

export function modifierRegleCollecte(id: number, m: Partial<SaisieRegleCollecte>): Promise<{ regle: RegleCollecte }> {
  return envoyer('PATCH', `${PIECES}/regles/${String(id)}`, m);
}

export function testerRegleCollecte(c: CriteresRegleCollecte): Promise<ApercuRegleCollecte> {
  return envoyer('POST', `${PIECES}/regles/tester`, c);
}

export function reglePropose(pieceId: number): Promise<{ regle: SaisieRegleCollecte }> {
  return envoyer('POST', `${PIECES}/${String(pieceId)}/regle-proposee`, {});
}

export interface SourceCollecte {
  readonly id: string;
  readonly type: 'freescout' | 'gmail';
  readonly libelle: string;
  readonly dansReglage: boolean;
  readonly accessible: boolean;
  readonly curseur: string | null;
  readonly derniereCampagne: string | null;
  readonly dernierEtat: string | null;
  readonly derniereErreur: string | null;
}

export interface BilanCampagne {
  readonly le: string;
  readonly messages: number;
  readonly pieces: number;
  readonly nouvelles: number;
  readonly doublons: number;
  readonly dejaClassees: number;
  readonly parNature: Readonly<Record<NaturePiece, number>>;
  readonly aTelecharger: number;
  readonly deposees: number;
  /** Pièces en attente triées à cette campagne (absent d'un bilan plus ancien). */
  readonly reprises?: number;
  /** Pièces écartées par Jev à cette campagne, à relire (absent d'un bilan plus ancien). */
  readonly ecarteesJev?: number;
  /** Pièces jointes laissées de côté, par motif (absent d'un bilan d'avant la 4.14.0 finale). */
  readonly ignorees?: Readonly<Record<string, number>>;
  readonly erreurs: readonly { source: string; message: string }[];
}

export interface EtatSources {
  readonly sources: SourceCollecte[];
  readonly erreursReglage: readonly { source: string; message: string }[];
  readonly acces: { freescout: boolean; gmail: boolean; drive: boolean; jev: boolean };
  /** Faux : mode à blanc, rien n'est déposé dans le Drive. */
  readonly depotActif: boolean;
  readonly campagne: { enCours: boolean; derniere: BilanCampagne | null };
}

export function sourcesCollecte(): Promise<EtatSources> {
  return lire(`${PIECES}/sources`);
}
